/**
 * ProductForm
 * @description Handles the add to cart from the product form
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.options = {};

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions
      };
    }

    // Elements
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.productID = parseInt(this.form.dataset.productId);
    this.cartNotification = document.querySelector('cart-notification');

    // Events
    this.form.addEventListener('submit', (event) => {
      this.onSubmitHandler(event);
    });

    // Cart object
    this.cartObj = () => {
      return fetch(`${routes.cart_url}`, {...fetchConfig() })
      .then((response) => response.json())
      .then((state) => {
        return state;
      })
      .catch((error) => debug() && console.error(error));
    }
  }

  /**
   * onSubmitHandler
   * @description Triggered when the form is submitted.
   * @param {Object} event
   */
  onSubmitHandler(event) {

    // Stop normal behavior
    event.preventDefault();

    let body;

    if (this.options.disableTieredPricing) {

      let formJSON = JSON.parse(serializeForm(this.form));

      body = JSON.stringify({
        ...formJSON,
        sections: this.cartNotification?.getSectionsToRender().map((section) => section.id),
        sections_url: window.location.pathname
      });

      // add new item to the cart
      this.addToCart(routes.cart_add_url, body);
    } else {

      let currentLine = 1;

      // Set active element
      this.cartNotification?.setActiveElement(document.activeElement);

      // Set the submit button to disabled state and add the loader
      this.submitButton.setAttribute('disabled', true);
      this.submitButton.classList.add('loading');

      // Set variables
      let isInCart = false;
      let updates = {};
      let oldQuantity;
      let properties = {};

      // Check if the current product is already in the cart and get the quantity for it
      this.cartObj()
      .then(cartObj => {
        for (let index = 0; index < cartObj.items.length; index++) {
          const element = cartObj.items[index];
          if (element.product_id === this.productID) {
            oldQuantity = parseInt(element.quantity);
            currentLine += index;

            // item is already in cart
            isInCart = true;
            break;
          }
        }
      }).then(() => {

        let formJSON = JSON.parse(serializeForm(this.form));
        // get form properties
        for (const key in formJSON) {
          const propertiesKeyMatch = key.substring(0, 11);
          if (propertiesKeyMatch === 'properties[') {
            const startIndex = key.indexOf('[') + 1;
            const endIndex = key.indexOf(']');
            const propertyKey = key.substring(startIndex, endIndex);
            const propertyValue = formJSON[key];
            properties[propertyKey] = propertyValue;
          }
        }
        formJSON.properties = properties;

        // If product is already in cart, loop through the variants object and find the tier. And if the tier is different from the one already in the cart, update the variant ID that gets added to the cart
        if (isInCart) {

          // Set required variables for cart update
          const inputQuantity = parseInt(this.form.querySelector('[name="quantity"]').value);
          const maxCartQuantity = parseInt(this.form.querySelector('[data-add-to-cart-quantity]').max);
          const productVariantsObj = JSON.parse(this.form.dataset.productTemplateFormVariants);
          let currentTierVariantId;
          let newQuantity = inputQuantity + oldQuantity;

          // Check: if new quantity exceeds max quantity, new quantity is the max quantity.
          if (newQuantity > maxCartQuantity) {
            newQuantity = maxCartQuantity;
          }

          // Loop through the variants
          if (productVariantsObj) {
            for (const [key,value] of Object.entries(productVariantsObj)) {
              const tier = key;

              // set the variant ID in the object to a quantity of 0
              updates[value] = 0;

              // Check if new quantity is above new tier and set the current tier
              if (newQuantity >= parseInt(tier)) {
                currentTierVariantId = productVariantsObj[key];
              }
            }
          }

          // Update the full updates object with the correct variant
          updates[currentTierVariantId] = newQuantity;

          body = JSON.stringify({
            updates,
            sections: this.cartNotification?.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname
          });

          // update the existing cart products
          this.addToCart(routes.cart_update_url, body, currentLine, newQuantity, formJSON);

        } else {

          // The object is not in the cart yet, so add it
          body = JSON.stringify({
            ...formJSON,
            sections: this.cartNotification?.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname
          });

          // add new item to the cart
          this.addToCart(routes.cart_add_url, body);
        }
    });
    }
  }

  /**
   * addToCart
   * @description Adds to cart or changes existing cart items
   * @param {string} route
   * @param {object} body
   * @param {productID} number
   */
  addToCart(route, body, line, quantity, formProperties = null) {
    // Add the product to the cart
    fetch(`${route}`, { ...fetchConfig('javascript'), body })
    .then((response) => response.json())
    .then((parsedState) => {
      this.cartNotification?.renderContents(parsedState, this.productID);
    })
    .catch((error) => debug() && console.error(error))
    .finally(() => {
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('disabled');

      // re-add quantity & properties to the changed variant
      if (formProperties !== null && quantity !== null) {
        const body = JSON.stringify({
          line,
          quantity,
          properties: {
            ...formProperties.properties
          }
        });

        fetch(routes.cart_change_url, { ...fetchConfig(), body })
        .then((response) => response.json())
        .catch((error) => debug() && console.error(error));
      }

    });
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
